/* eslint-disable camelcase */
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import {
  Button,
  InnerContent,
  ModalFooter,
  SimpleModal,
  SimpleModalHeader,
  SimpleModalTitle,
} from '@fountain/fountain-ui-components';
import Typography from '@material-ui/core/Typography';
import queryString from 'query-string';
import React, { useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import { wxExportApplicantAction } from 'containers/ApplicantActions/actions';
import makeSelectApplicantTableV2 from 'containers/ApplicantTableV2/selectors';
import { Applicant, BulkSelect } from 'containers/ApplicantTableV2/types';
import globalMessages from 'shared/global/messages';

import { messages } from './messages';

export interface WxExportApplicantModalProps {
  handleClose: () => void;
}

// Mirrored against BulkWxExport::EMPLOYMENT_STATUS_SUBTYPES by
// tools/contract-mirrors. 'temporary' is left out on purpose: WX accepts it,
// this picker does not offer it.
type EmploymentStatusType =
  | 'created'
  | 'preHire'
  | 'active'
  | 'rehire'
  | 'inactive'
  | 'terminated';

// Mirrored against WX's SubStatus enum by tools/contract-mirrors.
const employmentStatusSubtypes = [
  'applicant',
  'employee',
  'leaveOfAbsence',
  'suspended',
  'seasonal',
  'furloughed',
  'resigned',
  'involuntarySeparation',
  'fired',
  'archived',
] as const;

type EmploymentStatusSubtype = (typeof employmentStatusSubtypes)[number];

const employmentStatusSubtypesByType: Record<
  EmploymentStatusType,
  EmploymentStatusSubtype[]
> = {
  created: ['applicant', 'employee'],
  preHire: ['applicant'],
  active: ['employee'],
  rehire: ['applicant', 'employee'],
  inactive: ['leaveOfAbsence', 'suspended', 'seasonal', 'furloughed'],
  terminated: ['resigned', 'involuntarySeparation', 'fired', 'archived'],
};

const employmentStatusTypeMessages = {
  created: messages.created,
  preHire: messages.preHire,
  active: messages.active,
  rehire: messages.rehire,
  inactive: messages.inactive,
  terminated: messages.terminated,
};

const employmentStatusSubtypeMessages: Record<
  EmploymentStatusSubtype,
  typeof messages.applicant
> = {
  applicant: messages.applicant,
  employee: messages.employee,
  leaveOfAbsence: messages.leaveOfAbsence,
  suspended: messages.suspended,
  seasonal: messages.seasonal,
  furloughed: messages.furloughed,
  resigned: messages.resigned,
  involuntarySeparation: messages.involuntarySeparation,
  fired: messages.fired,
  archived: messages.archived,
};

export const WxExportApplicantModal: VFC<WxExportApplicantModalProps> = ({
  handleClose,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [employmentStatusType, setEmploymentStatusType] = useState<
    EmploymentStatusType | ''
  >('');
  const [employmentStatusSubtype, setEmploymentStatusSubtype] = useState('');

  const {
    bulkSelect,
    excludedApplicantIds,
    selectedApplicantIds,
    selectByCount,
    applicants,
    selectedFilterStageIdsWithFunnel,
    totalRows,
  } = useSelector(makeSelectApplicantTableV2());

  const location = useLocation();
  const match = matchPath(location.pathname, {
    path: '/:accountSlug/brands/:brandSlug/locations/:locationId/jobs/:jobId/v2/stages/:stageExternalId',
    exact: true,
    strict: false,
  });

  const selectedApplicant: Applicant | undefined = Object.values(applicants)[0];

  const selectAllorByCount =
    bulkSelect === BulkSelect.ALL ||
    bulkSelect === BulkSelect.COUNT ||
    undefined;

  let numberOfSelectedApplicants = 0;
  switch (bulkSelect) {
    case BulkSelect.ALL:
      numberOfSelectedApplicants = totalRows;
      break;
    case BulkSelect.SOME:
      numberOfSelectedApplicants =
        typeof selectedApplicantIds === 'string'
          ? totalRows - excludedApplicantIds.length
          : selectedApplicantIds.length - excludedApplicantIds.length;
      break;
    case BulkSelect.COUNT:
      numberOfSelectedApplicants = selectByCount ?? 0;
      break;
    default:
      numberOfSelectedApplicants = 0;
      break;
  }

  const {
    filter_set_id,
    temp_filter_id,
    duplicate_applicant_id,
    stage_conditions_filter_set_id,
  } = queryString.parse(location.search);
  const { query } = queryString.parse(location.search);

  const isMasterApplicantsView = !match;
  const onSubmit = () => {
    dispatch(
      wxExportApplicantAction(
        isMasterApplicantsView,
        selectedApplicantIds,
        excludedApplicantIds,
        filter_set_id,
        temp_filter_id,
        duplicate_applicant_id,
        stage_conditions_filter_set_id,
        selectByCount,
        selectedApplicant,
        selectedFilterStageIdsWithFunnel,
        selectAllorByCount && query,
        {
          success: intl.formatMessage(messages.submissionSuccess),
          error: intl.formatMessage(messages.submissionError),
        },
        employmentStatusType,
        employmentStatusSubtype,
      ),
    );
    handleClose();
  };

  return (
    <SimpleModal onClose={handleClose} maxWidth={600} blurOverlay={false}>
      <SimpleModalHeader onClose={handleClose}>
        <SimpleModalTitle>
          {intl.formatMessage(messages.title)}
        </SimpleModalTitle>
      </SimpleModalHeader>
      <InnerContent>
        <Typography color="textPrimary">
          <FormattedMessage
            {...messages.body}
            values={{ count: numberOfSelectedApplicants }}
          />
        </Typography>
        <FormControl fullWidth margin="normal" required>
          <InputLabel id="employment-status-type-label">
            <FormattedMessage {...messages.mainStatus} />
          </InputLabel>
          <Select
            labelId="employment-status-type-label"
            onChange={event => {
              setEmploymentStatusType(
                event.target.value as EmploymentStatusType,
              );
              setEmploymentStatusSubtype('');
            }}
            value={employmentStatusType}
          >
            {Object.entries(employmentStatusTypeMessages).map(
              ([statusType, statusMessage]) => (
                <MenuItem key={statusType} value={statusType}>
                  <FormattedMessage {...statusMessage} />
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>
        <FormControl
          disabled={!employmentStatusType}
          fullWidth
          margin="normal"
          required
        >
          <InputLabel id="employment-status-subtype-label">
            <FormattedMessage {...messages.secondaryStatus} />
          </InputLabel>
          <Select
            labelId="employment-status-subtype-label"
            onChange={event =>
              setEmploymentStatusSubtype(event.target.value as string)
            }
            value={employmentStatusSubtype}
          >
            {employmentStatusType &&
              employmentStatusSubtypesByType[employmentStatusType].map(
                statusSubtype => (
                  <MenuItem key={statusSubtype} value={statusSubtype}>
                    <FormattedMessage
                      {...employmentStatusSubtypeMessages[statusSubtype]}
                    />
                  </MenuItem>
                ),
              )}
          </Select>
        </FormControl>
      </InnerContent>
      <ModalFooter>
        <Button type="secondary" onClick={handleClose} size="small">
          <FormattedMessage {...globalMessages.cancel} />
        </Button>
        <Button
          autoWidth
          disabled={!employmentStatusType || !employmentStatusSubtype}
          onClick={onSubmit}
          size="small"
          type="primary"
        >
          <FormattedMessage {...messages.send} />
        </Button>
      </ModalFooter>
    </SimpleModal>
  );
};

export default WxExportApplicantModal;
